import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { Dropdown } from 'primereact/dropdown'
import { InputNumber, InputNumberPassThroughOptions } from 'primereact/inputnumber'
import { useRecoilValue } from 'recoil'
import { themeState } from '../../utils/state-atoms'
import { getAgGridTheme } from '../../utils/styling-constants'
import { generateDistinctColors } from '../../utils/colors'
import { useGetCoefficientBox } from '../../api/report-API'
import SvgXYChart from './plot/SvgXYChart'
import ProjectRunSelector4Submission from './ProjectRunSelector4Submission'
import { Button } from 'primereact/button'

type CoefficientPoint = {
  id?: string
  x: number
  y: number
  name: string
  isTarget?: boolean
}

type CoefficientTreeNode = {
  id: string
  parentId: string
  treeLevel: 'project' | 'run'
  name: string
  project?: string
  run?: string
  cdrun?: number
  clrun?: number
  clfrun?: number
  clrrun?: number
  csrun?: number
  cs?: number
  csf?: number
  csr?: number
  pcnt_frontrun?: number
}

const numberFormater = (value: number | string) => {
  if (value === null || value === undefined) {
    return ''
  }
  const numberValue = Number(value)
  if (Number.isNaN(numberValue)) {
    return numberValue.toString()
  }
  return numberValue.toFixed(3)
}

const inputBoxClasses = {
  root: {
    className:
      'px-1 py-1 !max-w-[100px] border border-solid border-gray-300 rounded-lg flex items-stretch justify-between'
  },
  input: {
    root: {
      className:
        'max-w-[70px] border-none pl-1 max-h-4 mt-2 !outline-none focus:!outline-none focus:!ring-0 focus:!shadow-none focus-visible:!outline-none focus-visible:!ring-0 focus-visible:!shadow-none'
    }
  },
  buttonGroup: { root: { className: 'h-full !px-1' } },
  incrementButton: { className: 'max-w-4' },
  decrementButton: { className: 'max-w-4' }
} as InputNumberPassThroughOptions

/*
  Data Shape for the data grid:
  {
    project: string,
    run: string,
    cdrun: number,
    clrun: number,
    clfrun: number,
    clrrun: number,
    csrun: number,
    pcnt_frontrun: number
  }
*/
const columns = [
  {
    field: 'cdrun',
    headerName: 'CD',
    sortable: true,
    suppressHeaderMenuButton: true,
    cellRendererParams: {
      padding: 'px-1'
    },
    cellRenderer: (rowData: any) => {
      return numberFormater(rowData?.value)
    }
  },
  {
    field: 'clrun',
    headerName: 'CL',
    sortable: true,
    suppressHeaderMenuButton: true,
    cellRendererParams: {
      padding: 'px-1'
    },
    cellRenderer: (rowData: any) => {
      return numberFormater(rowData?.value)
    }
  },
  {
    field: 'clfrun',
    headerName: 'CLF',
    sortable: true,
    suppressHeaderMenuButton: true,
    cellRendererParams: {
      padding: 'px-1'
    },
    cellRenderer: (rowData: any) => {
      return numberFormater(rowData?.value)
    }
  },
  {
    field: 'clrrun',
    headerName: 'CLR',
    sortable: true,
    suppressHeaderMenuButton: true,
    cellRendererParams: {
      padding: 'px-1'
    },
    cellRenderer: (rowData: any) => {
      return numberFormater(rowData?.value)
    }
  },
  {
    field: 'csrun',
    headerName: 'CS',
    sortable: true,
    suppressHeaderMenuButton: true,
    cellRendererParams: {
      padding: 'px-1'
    },
    cellRenderer: (rowData: any) => {
      return numberFormater(rowData?.value)
    }
  },
  {
    field: 'pcnt_frontrun',
    headerName: '% FRNT',
    sortable: true,
    suppressHeaderMenuButton: true,
    cellRendererParams: {
      padding: 'px-1'
    },
    cellRenderer: (rowData: any) => {
      return numberFormater(rowData?.value)
    }
  }
]

const TICK_STEP = 0.001

const SubmissionCoefficient: FC = () => {
  const [targetCDInputValue, setTargetCDInputValue] = useState<number | null>(() => {
    return localStorage.getItem('targetCD')
      ? parseFloat(localStorage.getItem('targetCD') as string)
      : 0
  })
  const [targetCLInputValue, setTargetCLInputValue] = useState<number | null>(() => {
    return localStorage.getItem('targetCL')
      ? parseFloat(localStorage.getItem('targetCL') as string)
      : 0
  })
  const [targetRunIdInputValue, setTargetRunIdInputValue] = useState<string | null>(() =>
    localStorage.getItem('targetRunId')
  )
  const [targetCDInput, setTargetCDInput] = useState<number | null>(targetCDInputValue)
  const [targetCLInput, setTargetCLInput] = useState<number | null>(targetCLInputValue)
  const [targetRunId, setTargetRunId] = useState<string | null>(targetRunIdInputValue)
  const appTheme = useRecoilValue(themeState)
  const [coefficientBoxData, setCoefficientBoxData] = useState<any[]>([])
  const [coefficientData, setCoefficientData] = useState<any[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const gridRef = useRef<AgGridReact>(null)

  const [{ loading: loadingCoefficientBox }, getCoefficientEnvelope] = useGetCoefficientBox()

  const xAxis = 'cdrun'
  const xName = 'CD'
  const yAxis = 'clrun'
  const yName = 'CL'

  const [selectedRuns, setSelectedRuns] = useState<string[]>([])
  const [selectedProjects, setSelectedProjects] = useState<string[]>([])
  const [hoveredRunId, setHoveredRunId] = useState<string | null>(null)

  const treeDataRows = useMemo(() => {
    const rows: CoefficientTreeNode[] = []
    const projectSet = new Set<string>()

    coefficientData.forEach((item: any) => {
      const project = String(item?.project || '')
      const run = String(item?.run || '')

      if (!project || !run) {
        return
      }

      if (!projectSet.has(project)) {
        projectSet.add(project)
        rows.push({
          id: project,
          parentId: '',
          treeLevel: 'project',
          name: project,
          project
        })
      }

      rows.push({
        id: `${project}::${run}`,
        parentId: project,
        treeLevel: 'run',
        name: run,
        project,
        run,
        cdrun: item?.cdrun,
        clrun: item?.clrun,
        clfrun: item?.clfrun,
        clrrun: item?.clrrun,
        csrun: item?.csrun,
        cs: item?.cs,
        csf: item?.csf,
        csr: item?.csr,
        pcnt_frontrun: item?.pcnt_frontrun ?? item?.pct_frontrun
      })
    })

    return rows
  }, [coefficientData])

  const hasExternalSelectionFilter = true

  const isRunSelected = (project: string, run: string) => {
    const scopedRunKey = `${project}::${run}`
    return selectedRuns.includes(scopedRunKey) || selectedRuns.includes(run)
  }

  const targetRunOptions = useMemo(
    () =>
      treeDataRows
        .filter(
          row =>
            row.treeLevel === 'run' &&
            selectedProjects.includes(row.project || '') &&
            (selectedRuns.length === 0 || isRunSelected(row.project || '', row.run || ''))
        )
        .map(row => ({ label: row.run || '', value: row.id })),
    [treeDataRows, selectedProjects, selectedRuns]
  )

  const targetRun = useMemo(
    () => treeDataRows.find(row => row.id === targetRunId) || null,
    [treeDataRows, targetRunId]
  )

  useEffect(() => {
    const selectedRun = treeDataRows.find(row => row.id === targetRunIdInputValue)
    const selectedCD = Number(selectedRun?.cdrun)
    const selectedCL = Number(selectedRun?.clrun)

    if (Number.isFinite(selectedCD)) {
      setTargetCDInputValue(selectedCD)
    }
    if (Number.isFinite(selectedCL)) {
      setTargetCLInputValue(selectedCL)
    }

    if (selectedRun && Number.isFinite(selectedCD) && Number.isFinite(selectedCL)) {
      setTargetRunId(selectedRun.id)
      setTargetCDInput(selectedCD)
      setTargetCLInput(selectedCL)
      localStorage.setItem('targetRunId', selectedRun.id)
      localStorage.setItem('targetCD', selectedCD.toString())
      localStorage.setItem('targetCL', selectedCL.toString())
    }
  }, [treeDataRows, targetRunIdInputValue])

  const targetCD = useMemo(() => {
    return targetRun && Number.isFinite(targetCDInput) ? targetCDInput : null
  }, [targetRun, targetCDInput])

  const targetCL = useMemo(() => {
    return targetRun && Number.isFinite(targetCLInput) ? targetCLInput : null
  }, [targetRun, targetCLInput])

  useEffect(() => {
    if (targetCD === null || targetCL === null) {
      return
    }

    getCoefficientEnvelope({ cd: targetCD, cl: targetCL } as any)
      .then(res => {
        if (res.data) {
          console.log('Coefficient Envelope Data:', res.data)
          setCoefficientBoxData(res.data)
        }
      })
      .catch(err => {
        console.error('Error fetching coefficient envelope:', err)
      })
  }, [targetCD, targetCL])

  const numericPoints = useMemo(
    () =>
      coefficientBoxData
        .filter(point => Number.isFinite(point?.cd) && Number.isFinite(point?.cl))
        .map((point: any) => ({ x: Number(point.cd), y: Number(point.cl) })) as CoefficientPoint[],
    [coefficientBoxData]
  )

  const scatterPoints = useMemo(() => {
    const points: CoefficientPoint[] = []

    if (Number.isFinite(targetCD) && Number.isFinite(targetCL)) {
      points.push({
        id: 'target',
        x: Number(targetCD),
        y: Number(targetCL),
        name: 'Target',
        isTarget: true
      })
    }

    treeDataRows.forEach(row => {
      if (row.treeLevel !== 'run') {
        return
      }

      const project = String(row.project || '')
      const run = String(row.run || '')
      if (!project || !run) {
        return
      }

      if (!selectedProjects.includes(project)) {
        return
      }

      if (selectedRuns.length > 0 && !isRunSelected(project, run)) {
        return
      }

      const rowCD = Number(row.cdrun)
      const rowCL = Number(row.clrun)

      if (!Number.isFinite(rowCD) || !Number.isFinite(rowCL)) {
        return
      }

      points.push({ id: row.id, x: rowCD, y: rowCL, name: run })
    })

    return points
  }, [targetCD, targetCL, treeDataRows, selectedProjects, selectedRuns])

  const nonTargetScatterColors = useMemo(
    () => generateDistinctColors(scatterPoints.filter(point => !point.isTarget).length),
    [scatterPoints]
  )

  const runScatterColors = useMemo(() => {
    const colors = new Map<string, string>()
    const nonTargetPoints = scatterPoints.filter(point => !point.isTarget)

    treeDataRows.forEach(row => {
      if (row.treeLevel !== 'run' || !row.project || !row.run) {
        return
      }

      const rowCD = Number(row.cdrun)
      const rowCL = Number(row.clrun)
      if (!Number.isFinite(rowCD) || !Number.isFinite(rowCL)) {
        return
      }

      const matchingPoint = scatterPoints.find(point => point.id === row.id)
      if (!matchingPoint) {
        return
      }

      const color = matchingPoint.isTarget
        ? '#25eb63'
        : nonTargetScatterColors[nonTargetPoints.indexOf(matchingPoint)]
      colors.set(`${row.project}::${row.run}`, color)
    })

    return colors
  }, [scatterPoints, treeDataRows, nonTargetScatterColors])

  const nonTargetLegendItems = useMemo(
    () =>
      scatterPoints
        .filter(point => !point.isTarget)
        .map((point, index) => ({
          id: point.id,
          name: point.name,
          color: nonTargetScatterColors[index % (nonTargetScatterColors.length || 1)]
        })),
    [scatterPoints, nonTargetScatterColors]
  )

  const doesExternalFilterPass = (node: any) => {
    if (!node?.data) {
      return false
    }

    if (selectedProjects.length === 0 && selectedRuns.length === 0) {
      return false
    }

    const level = String(node.data.treeLevel || '')
    const project = String(node.data.project || '')
    const run = String(node.data.run || node.data.name || '')

    if (level === 'project') {
      if (!selectedProjects.includes(project)) {
        return false
      }

      if (selectedRuns.length === 0) {
        return true
      }

      return treeDataRows.some(
        row =>
          row.treeLevel === 'run' &&
          row.project === project &&
          isRunSelected(project, row.run || '')
      )
    }

    if (level !== 'run') {
      return false
    }

    if (!selectedProjects.includes(project)) {
      return false
    }

    if (selectedRuns.length === 0) {
      return true
    }

    return isRunSelected(project, run)
  }

  useEffect(() => {
    gridRef.current?.api?.onFilterChanged()
  }, [selectedProjects, selectedRuns])

  useEffect(() => {
    gridRef.current?.api?.redrawRows()
  }, [hoveredRunId])

  const gridColumns = useMemo(() => [...columns], [])
  const gridTheme = useMemo(() => getAgGridTheme(appTheme), [appTheme])

  return (
    <div className="flex items-stretch gap-4 w-full h-full bg-white dark:bg-neutral-90 ">
      <div>
        <ProjectRunSelector4Submission
          showFilter={false}
          onDataLoading={() => setLoading(true)}
          onDoneLoading={data => {
            setLoading(false)
            if (data) {
              setCoefficientData(data)
              console.log('Coefficient Data:', data)
            }
          }}
          initialProjectRunSelection={null}
          onProjectRunSelectionChanged={(projects, runs) => {
            setSelectedProjects(projects)
            setSelectedRuns(runs)
          }}
        />
      </div>
      <div className="flex basis-0 grow flex-col items-stretch gap-4 px-4 py-4 text-gray-700 dark:text-neutral-10 min-h-full">
        <div className="min-h-[150px] basis-0 grow w-full">
          <AgGridReact
            ref={gridRef}
            className="h-full w-full"
            theme={gridTheme}
            rowData={treeDataRows}
            treeData={true}
            excludeChildrenWhenTreeDataFiltering={true}
            treeDataParentIdField="parentId"
            getRowId={params => String(params.data.id)}
            columnDefs={gridColumns}
            autoGroupColumnDef={{
              headerName: 'Project / Run',
              minWidth: 220,
              suppressHeaderMenuButton: true,
              cellRendererParams: {
                suppressCount: true,
                innerRenderer: (params: any) => {
                  const isProjectRow = params?.data?.treeLevel === 'project'
                  const isExpanded = Boolean(params?.node?.expanded)

                  return (
                    <div className="flex items-center gap-2">
                      {isProjectRow && (
                        <button
                          type="button"
                          className="flex h-5 w-5 items-center justify-center rounded text-neutral-70 hover:bg-neutral-20 dark:text-neutral-10 dark:hover:bg-neutral-80"
                          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} project ${
                            params?.value || ''
                          }`}
                          onClick={(event: any) => {
                            event.preventDefault()
                            event.stopPropagation()
                            params?.node?.setExpanded(!isExpanded)
                          }}>
                          <i className={`pi ${isExpanded ? 'pi-minus' : 'pi-plus'} text-xs`} />
                        </button>
                      )}
                      <span
                        style={{
                          color:
                            params?.data?.treeLevel === 'run'
                              ? runScatterColors.get(params.data.id)
                              : undefined
                        }}>
                        {params?.data.name}
                      </span>
                    </div>
                  )
                }
              }
            }}
            defaultColDef={{
              flex: 1,
              minWidth: 90,
              resizable: true
            }}
            rowHeight={24}
            groupDefaultExpanded={1}
            loading={loading}
            animateRows={true}
            getRowStyle={(params: any) =>
              params.data?.id === hoveredRunId
                ? {
                    backgroundColor: appTheme === 'dark' ? '#374151' : '#fef3c7'
                  }
                : undefined
            }
            isExternalFilterPresent={() => hasExternalSelectionFilter}
            doesExternalFilterPass={doesExternalFilterPass}
          />
        </div>
        <div className="flex flex-row items-center justify-between gap-4">
          <div className="flex-grow flex flex-row gap-4 items-center border border-solid border-gray-300 rounded px-4 py-2 dark:bg-transparent dark:border-neutral-70 dark:text-neutral-10">
            <div className="flex flex-row gap-1 items-center">
              <label htmlFor="targetRun" className="shrink-0">
                Target Run:
              </label>
              <Dropdown
                inputId="targetRun"
                value={targetRunIdInputValue}
                options={targetRunOptions}
                onChange={e => setTargetRunIdInputValue(e.value)}
                placeholder="Select a run"
                showClear
                filter
                className="w-48"
                pt={{
                  root: { className: 'border border-solid !border-gray-300 rounded-lg' },
                  input: { className: 'px-2 !py-[6px]' }
                }}
              />
            </div>
            <div className="flex flex-row gap-1 items-center">
              <label htmlFor="targetCD" className="shrink-0">
                Target CD:
              </label>
              <InputNumber
                id="targetCD"
                value={targetCDInputValue ?? null}
                step={0.001}
                showButtons
                inputMode="decimal"
                onValueChange={e => setTargetCDInputValue(e.value ?? null)}
                minFractionDigits={0}
                maxFractionDigits={3}
                pt={inputBoxClasses}
              />
            </div>
            <div className="flex flex-row gap-1 items-center">
              <label htmlFor="targetCL" className="shrink-0">
                Target CL:
              </label>
              <InputNumber
                id="targetCL"
                step={0.001}
                showButtons
                inputMode="decimal"
                value={targetCLInputValue}
                onValueChange={e => setTargetCLInputValue(e.value ?? null)}
                minFractionDigits={0}
                maxFractionDigits={3}
                pt={inputBoxClasses}
              />
            </div>
            <Button
              label="Update"
              loading={loadingCoefficientBox}
              onClick={() => {
                setTargetRunId(targetRunIdInputValue)
                setTargetCDInput(targetCDInputValue)
                setTargetCLInput(targetCLInputValue)
                localStorage.setItem('targetRunId', targetRunIdInputValue || '')
                localStorage.setItem('targetCD', targetCDInputValue?.toString() || '0')
                localStorage.setItem('targetCL', targetCLInputValue?.toString() || '0')
              }}
              className="h-8 px-3"
            />
          </div>
        </div>

        <div className="flex flex-row flex-wrap gap-4 items-center justify-center">
          {nonTargetLegendItems.map(item => (
            <div
              key={item.id || item.name}
              className="flex items-center gap-1.5"
              onMouseEnter={() => setHoveredRunId(item.id || null)}
              onMouseLeave={() => setHoveredRunId(null)}>
              <span
                className="h-2.5 w-2.5 rounded-full shrink-0"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-sm">{item.name}</span>
            </div>
          ))}
        </div>
        <div className="min-h-[calc(50vh-200px)] max-h-[70%] w-full basis-0 grow">
          <SvgXYChart
            className="w-full h-full"
            polygonPoints={numericPoints}
            scatterPoints={scatterPoints}
            scatterPointColor="#25eb63"
            nonTargetScatterPointColor={nonTargetScatterColors}
            xInterval={TICK_STEP}
            yInterval={TICK_STEP}
            xTickPadding={1}
            yTickPadding={2}
            xLabel={xName || xAxis}
            yLabel={yName || yAxis}
            darkMode={appTheme === 'dark'}
            onScatterPointHover={point =>
              setHoveredRunId(point?.isTarget ? null : point?.id || null)
            }
          />
        </div>
      </div>
    </div>
  )
}

export default SubmissionCoefficient
